import type { MouseEvent, SyntheticEvent } from 'react';
import { useEffect, useState } from 'react';
import { Preferences } from '@capacitor/preferences';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { updateUser } from 'features/auth/api/updateUserData';
import { selectUser } from 'features/auth/store/authSelectors';
import { selectShowDeveloperFeatures } from 'features/dev-tools/store/devToolsSelectors';
import { setShowDeveloperFeatures } from 'features/dev-tools/store/devToolsSlice';
import {
  devRemoveAllParticipationsFunction,
  devRemoveParticipationFunction,
} from 'features/firebase/firebase';
import { appLocales, languageNames } from 'features/i18n/locales';
import { datadogRumAddCustomAction } from 'features/monitoring/customDatadogRumActions';
import { selectOptionalParticipationDocData } from 'features/participations/store/participationSelectors';
import { RoutePath } from 'features/routing/constants';
import { useRouteMatch } from 'features/routing/useRouteMatch';
import { useAppDispatch, useAppSelector } from 'features/store/hooks';
import { DevQuestionnaireMenu } from './DevQuestionnaireMenu';

// reloadOnChange prop is needed for the questionnaire view, as when we change language via dev tools
// on questionnaire, not everything works as expected. This is not real user scenario to have language changed mid questionnaire,
// so instead of trying to adapt prod code to dev tools needs, we rather reload the page on lang change.
export const DevTools = ({ reloadOnChange }: { reloadOnChange?: boolean }) => {
  const questionnaireMatch = useRouteMatch([RoutePath.ParticipationTask()]);
  const protocolEditorMatch = useRouteMatch([
    RoutePath.ProtocolEdit(),
    RoutePath.ProtocolEditWithoutId,
  ]);

  const devMenuSlice = questionnaireMatch
    ? 'questionnaire'
    : protocolEditorMatch
      ? 'adminProtocolEditor'
      : undefined;

  const showDeveloperFeatures = useAppSelector(selectShowDeveloperFeatures);
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const participationId = useAppSelector(
    selectOptionalParticipationDocData
  )?.id;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const loadedLanguage = i18n.language;
  const [formData, setFormData] = useState({
    language: loadedLanguage,
  });

  const handleChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (loadedLanguage !== formData.language) {
      datadogRumAddCustomAction({
        name: 'userChangeLanguage',
        details: {
          from: loadedLanguage,
          to: formData.language,
        },
      });

      updateUser(user.uid, { language: formData.language })
        .then(() => {
          if (reloadOnChange) {
            window.location.reload();
          } else {
            toast.success(t('UserUpdated'));
          }
        })
        .catch((error: unknown) => {
          console.log(error);
          toast.error(t('ErrorUpdatingUser'));
        });
    }
  };

  useEffect(() => {
    setFormData({ language: loadedLanguage });
  }, [loadedLanguage]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const devActionToasts = {
    pending: t('Processing'),
    success: t('Success'),
    error: { render: () => t('Failure') },
  };

  const handleRemoveParticipation = () => {
    if (!participationId) {
      return;
    }

    void toast.promise(
      devRemoveParticipationFunction({ participationId }).then(() => {
        handleClose();
        void navigate(RoutePath.StudyChoice);
      }),
      devActionToasts
    );
  };

  const handleRemoveAllParticipations = () => {
    void toast.promise(devRemoveAllParticipationsFunction(), devActionToasts);
  };

  if (import.meta.env.PUBLIC_ALLOW_DEVELOPER_FEATURES !== 'true') {
    return <div />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row-reverse', gap: 1 }}>
      <ToggleButton
        aria-label="toggle dev features"
        color="primary"
        value="check"
        size="small"
        selected={showDeveloperFeatures}
        onChange={async () => {
          dispatch(setShowDeveloperFeatures(!showDeveloperFeatures));

          if (!showDeveloperFeatures) {
            datadogRumAddCustomAction({
              name: 'appEnableDeveloperFeatures',
            });
          }

          await Preferences.set({
            key: 'showDeveloperFeatures',
            value: showDeveloperFeatures ? 'false' : 'true',
          });
        }}
      >
        <DeveloperModeIcon />
      </ToggleButton>
      {showDeveloperFeatures && (
        <>
          {devMenuSlice && <DevQuestionnaireMenu slice={devMenuSlice} />}
          <IconButton
            aria-label="dev menu"
            id="settings-button"
            aria-controls={open ? 'dev-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            onClick={handleClick}
          >
            <SettingsIcon />
          </IconButton>
          <Menu
            id="dev-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
          >
            <Stack direction="column" spacing={2} px={2} py={1} minWidth={360}>
              <Typography variant="body2" fontWeight="700">
                UID: {user.uid}
              </Typography>
              <Stack
                component="form"
                direction="row"
                alignItems="center"
                onSubmit={handleSubmit}
                spacing={2}
              >
                <FormControl sx={{ flexGrow: 1 }}>
                  <InputLabel id="language-select-label" size="small">
                    {t('Language')}
                  </InputLabel>
                  <Select
                    labelId="language-select-label"
                    id="language"
                    name="language"
                    label={t('Language')}
                    size="small"
                    value={formData.language}
                    onChange={handleChange}
                  >
                    {appLocales.map(locale => (
                      <MenuItem key={locale} value={locale}>
                        {languageNames[locale]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button type="submit" variant="contained" size="medium">
                  {t('Save')}
                </Button>
              </Stack>
              {participationId && (
                <>
                  <Divider />
                  <Button
                    fullWidth
                    variant="outlined"
                    color="warning"
                    onClick={handleRemoveParticipation}
                  >
                    Remove this participation
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    onClick={handleRemoveAllParticipations}
                  >
                    Remove all participations
                  </Button>
                </>
              )}
            </Stack>
          </Menu>
        </>
      )}
    </Box>
  );
};
